import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { ListSubheader } from '@mui/material';
import { useMenuAnchor } from '~/hooks/useMenuAnchor';
import { PickableImage } from '../adapters/types';
import ConsoleMenu from '../ConsoleMenu';
import { sharesNameWithAnotherSeries } from './tileViewModel';
import ImageMenuRow from './ImageMenuRow';
import ImageNameTrigger from './ImageNameTrigger';

interface ImageChoiceMenuProps {
    /**
     * The image the control names and the list marks. Left out, the control
     * says `placeholder` and the list marks nothing.
     */
    readonly current?: PickableImage;
    /** What the control says while it names no image. Not PHI. */
    readonly placeholder?: string;
    /** The images to choose from, in the order given. */
    readonly images: ReadonlyArray<PickableImage>;
    /**
     * Called with the chosen image, `current` too: whether choosing it again
     * changes anything is the caller's to decide.
     */
    readonly onChoose: (seriesInstanceUid: SeriesInstanceUid) => void;
    readonly ariaLabel: string;
    readonly testId: string;
    /**
     * Why the list does not open, when it does not. The control stays,
     * disabled, and the reason shows above it.
     */
    readonly disabledReason?: string;
    /** A heading above the images. */
    readonly section?: string;
}

/**
 * An image named as the control that changes it, and the list of images it
 * opens. Each image in the list is named by its modality and its name
 * (masked with the privacy toggle), with its series number while names are
 * hidden.
 */
export default function ImageChoiceMenu({
    current,
    placeholder,
    images,
    onChoose,
    ariaLabel,
    testId,
    disabledReason,
    section,
}: ImageChoiceMenuProps) {
    const menu = useMenuAnchor();
    return (
        <>
            <ImageNameTrigger
                modality={current?.modality}
                label={current?.label}
                placeholder={placeholder}
                ariaLabel={ariaLabel}
                open={menu.open}
                disabledReason={disabledReason}
                onOpen={menu.onOpen}
                testId={testId}
            />
            <ConsoleMenu
                anchorEl={menu.anchorEl}
                open={menu.open}
                onClose={menu.onClose}
            >
                {section !== undefined ? (
                    <ListSubheader>{section}</ListSubheader>
                ) : null}
                {images.map((image) => {
                    const isCurrent =
                        image.seriesInstanceUid === current?.seriesInstanceUid;
                    return (
                        <ImageMenuRow
                            key={image.seriesInstanceUid}
                            selected={isCurrent}
                            onClick={() => {
                                menu.onClose();
                                onChoose(image.seriesInstanceUid);
                            }}
                            modality={image.modality}
                            label={image.label}
                            seriesNumber={image.seriesNumber}
                            sharesName={sharesNameWithAnotherSeries(
                                image,
                                images
                            )}
                        />
                    );
                })}
            </ConsoleMenu>
        </>
    );
}
